import {
  ERROR_CODES,
  type ApiErrorBody,
  type Locale,
  type PaginatedResponse,
  type PortalInvoiceDetailView,
  type PortalInvoiceView,
  type PortalMeView,
  type PortalReportView,
  type PortalStatementView,
  type PortalWorkOrderDetailView,
  type PortalWorkOrderView,
} from '@lims/shared-types';
import { ApiError } from '../api/client.js';

/**
 * The CLIENT PORTAL's own API client — Phase 11 (contract P, W3).
 *
 * Deliberately separate from `api/client.ts`:
 *
 *  - its session lives under its OWN storage key (`lims.portal.session`) and
 *    never reads or writes the staff keys;
 *  - it sends the portal access token ONLY to `/portal/**`, and nothing it
 *    sends ever carries a staff token — the staff client, in turn, never reads
 *    this key. A browser signed in as both still keeps the two apart;
 *  - it refreshes through `/portal/auth/refresh`, never the staff route.
 *
 * The backend is the control: a staff token is refused on every portal route
 * and a portal token on every staff route. This module only avoids offering
 * the wrong one.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const PORTAL_SESSION_KEY = 'lims.portal.session';

interface StoredSession {
  readonly accessToken: string;
  readonly refreshToken: string;
}

interface TokenPair {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly tokenType: string;
  readonly expiresIn: number;
}

export const portalSession = {
  read(): StoredSession | null {
    try {
      const raw = localStorage.getItem(PORTAL_SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<StoredSession>;
      return typeof parsed.accessToken === 'string' && typeof parsed.refreshToken === 'string'
        ? { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken }
        : null;
    } catch {
      return null;
    }
  },
  store(tokens: StoredSession): void {
    try {
      localStorage.setItem(
        PORTAL_SESSION_KEY,
        JSON.stringify({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken }),
      );
    } catch {
      // Storage unavailable (private mode): the session lasts for this page only.
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(PORTAL_SESSION_KEY);
    } catch {
      // Nothing stored, nothing to clear.
    }
  },
};

let onPortalSessionLost: (() => void) | null = null;

export function setPortalSessionLostHandler(handler: (() => void) | null): void {
  onPortalSessionLost = handler;
}

function buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  return new ApiError(
    body?.error?.code ?? ERROR_CODES.INTERNAL_ERROR,
    response.status,
    body?.error?.message ?? 'The request failed.',
    body?.error?.details ?? [],
    body?.error?.requestId,
  );
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshPortalSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const stored = portalSession.read();
    if (!stored) return false;
    try {
      const response = await fetch(buildUrl('/portal/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: stored.refreshToken }),
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { tokens: TokenPair };
      portalSession.store(body.tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

interface PortalRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | undefined>;
  /** Sign-in, refresh, password reset and branding send no token at all. */
  readonly anonymous?: boolean;
  readonly retrying?: boolean;
}

async function send(path: string, options: PortalRequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  // Only a portal path ever carries the portal token.
  if (!options.anonymous && path.startsWith('/portal/')) {
    const stored = portalSession.read();
    if (stored) headers.Authorization = `Bearer ${stored.accessToken}`;
  }
  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    });
  } catch {
    throw new ApiError('NETWORK_ERROR', 0, 'The server could not be reached.');
  }
}

async function portalRequest<T>(path: string, options: PortalRequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (response.status === 401 && !options.anonymous && !options.retrying) {
    if (await refreshPortalSession()) {
      return portalRequest<T>(path, { ...options, retrying: true });
    }
    portalSession.clear();
    onPortalSessionLost?.();
  }

  if (response.status === 204 || response.status === 202) {
    return (response.status === 202 ? await response.json().catch(() => undefined) : undefined) as T;
  }
  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}

async function portalDownload(path: string, retrying = false): Promise<Blob> {
  const response = await send(path, {});
  if (response.status === 401 && !retrying) {
    if (await refreshPortalSession()) return portalDownload(path, true);
    portalSession.clear();
    onPortalSessionLost?.();
  }
  if (!response.ok) throw await toApiError(response);
  return response.blob();
}

export interface PortalBranding {
  readonly tenant: { readonly nameAr: string; readonly nameEn: string } | null;
}

export const portalApi = {
  /** The laboratory's name before sign-in. Anonymous: no token of any kind. */
  branding: () => portalRequest<PortalBranding>('/auth/branding', { anonymous: true }),

  login: async (email: string, password: string): Promise<PortalMeView> => {
    const answer = await portalRequest<{ tokens: TokenPair; account: PortalMeView }>(
      '/portal/auth/login',
      { method: 'POST', body: { email, password }, anonymous: true },
    );
    portalSession.store(answer.tokens);
    return answer.account;
  },

  /** Revokes the session on the server, then forgets it here whatever the answer. */
  logout: async (): Promise<void> => {
    try {
      if (portalSession.read()) {
        await portalRequest<void>('/portal/auth/logout', { method: 'POST', body: {}, retrying: true });
      }
    } finally {
      portalSession.clear();
    }
  },

  /** Always accepted: the answer never says whether the address exists. */
  requestPasswordReset: (email: string) =>
    portalRequest<{ status: string }>('/portal/auth/password-reset', {
      method: 'POST',
      body: { email },
      anonymous: true,
    }),

  confirmPasswordReset: (token: string, password: string) =>
    portalRequest<void>('/portal/auth/password-reset/confirm', {
      method: 'POST',
      body: { token, password },
      anonymous: true,
    }),

  me: () => portalRequest<PortalMeView>('/portal/me'),

  updateMe: (preferredLocale: Locale) =>
    portalRequest<PortalMeView>('/portal/me', { method: 'PATCH', body: { preferredLocale } }),

  workOrders: (query: { page?: number; pageSize?: number; status?: string }) =>
    portalRequest<PaginatedResponse<PortalWorkOrderView>>('/portal/work-orders', { query }),

  workOrder: (id: string) =>
    portalRequest<PortalWorkOrderDetailView>(`/portal/work-orders/${encodeURIComponent(id)}`),

  reports: (query: { page?: number; pageSize?: number }) =>
    portalRequest<PaginatedResponse<PortalReportView>>('/portal/reports', { query }),

  report: (id: string) => portalRequest<PortalReportView>(`/portal/reports/${encodeURIComponent(id)}`),

  reportFile: (id: string, versionNumber: number) =>
    portalDownload(`/portal/reports/${encodeURIComponent(id)}/versions/${versionNumber}/file`),

  invoices: (query: { page?: number; pageSize?: number; status?: string }) =>
    portalRequest<PaginatedResponse<PortalInvoiceView>>('/portal/invoices', { query }),

  invoice: (id: string) =>
    portalRequest<PortalInvoiceDetailView>(`/portal/invoices/${encodeURIComponent(id)}`),

  statement: (query: { currency?: string; from?: string; to?: string }) =>
    portalRequest<PortalStatementView>('/portal/statement', { query }),
};
